<template>
  <div class="comments-list">
    <!-- Add Comment Button -->
    <div class="add-comment-section">
      <button
        class="btn-add-comment"
        @click="handleAddComment"
        :disabled="loadingSubmission"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        <span>Add Comment</span>
      </button>
    </div>

    <!-- Loading state -->
    <div v-if="loading && comments.length === 0" class="loading-state">
      <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
      <p>Loading comments...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="comments.length === 0" class="empty-state">
      <font-awesome-icon :icon="['fas', 'comments']" size="2x" />
      <p>No comments yet. Be the first to comment!</p>
    </div>

    <!-- Comments list -->
    <div v-else class="comments-container">
      <CommentItem
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        :depth="0"
      />
    </div>

    <!-- Load more trigger -->
    <div v-if="hasMore" id="load-more-trigger" class="load-more">
      <div v-if="loadingMore" class="loading-more">
        <font-awesome-icon :icon="['fas', 'spinner']" spin />
        Loading more...
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Submission from '@/api/Submission.js'
import SubmissionComposer from '@/components/SubmissionComposer.vue'
import CommentItem from './CommentItem.vue'
import { useMainStore } from '@/stores/main'

const props = defineProps({
  submissionId: {
    type: Number,
    required: true
  }
})

const mainStore = useMainStore()

// State
const comments = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const loadingSubmission = ref(false)
const hasMore = ref(false)
const offset = ref(0)
const parentSubmission = ref(null)
const limit = 20

// Observer for infinite scroll
let observer = null

// Fetch parent submission details
async function fetchParentSubmission() {
  loadingSubmission.value = true
  try {
    const response = await Submission.getSubmission(props.submissionId)
    if (response.status === 'success') {
      parentSubmission.value = response.data
    }
  } catch (error) {
    console.error('Failed to fetch submission:', error)
  } finally {
    loadingSubmission.value = false
  }
}

// Fetch comments
async function fetchComments(append = false) {
  if (!append) {
    loading.value = true
    offset.value = 0
    comments.value = []
  } else {
    loadingMore.value = true
  }

  try {
    const response = await Submission.getSubmissionComments(
      props.submissionId,
      offset.value,
      limit
    )

    if (response.status === 'success') {
      // Handle nested response structure
      const responseData = response.data || {}
      const commentsArray = responseData.comments || []

      // Filter out empty comments
      const newComments = commentsArray.filter(
        comment => comment && comment.content && comment.content.trim() !== ''
      )

      if (append) {
        comments.value = [...comments.value, ...newComments]
      } else {
        comments.value = newComments
      }

      hasMore.value = responseData.has_more || false
      offset.value = offset.value + newComments.length
    }
  } catch (error) {
    console.error('Failed to fetch comments:', error)
    console.error('Submission ID:', props.submissionId)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// Handle add comment button click
function handleAddComment() {
  if (!parentSubmission.value) {
    console.error('Parent submission not loaded')
    return
  }

  mainStore.show_modal(SubmissionComposer, {
    parentSubmission: parentSubmission.value,
    placeholder: `Reply to ${parentSubmission.value.user.name}...`,
    maxChars: 2000
  })
}

// Handle submission created event
function handleSubmissionCreated() {
  // Refresh comments list when a new comment is added
  fetchComments(false)
}

// Setup intersection observer for infinite scroll
function setupIntersectionObserver() {
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMore.value && !loadingMore.value) {
      fetchComments(true) // true = append mode
    }
  }, { threshold: 0.1 })

  const trigger = document.querySelector('#load-more-trigger')
  if (trigger) {
    observer.observe(trigger)
  }
}

// Lifecycle hooks
onMounted(async () => {
  // Fetch parent submission first
  await fetchParentSubmission()

  // Then fetch comments
  await fetchComments(false)

  // Setup intersection observer after initial render
  setTimeout(() => {
    setupIntersectionObserver()
  }, 100)

  // Listen for submission-created event
  mainStore.event_on('submission-created', handleSubmissionCreated)
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Add Comment Section */
.add-comment-section {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-add-comment {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #a855f7, #9333ea);
  border: none;
  border-radius: 0.5rem;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.btn-add-comment:hover:not(:disabled) {
  background: linear-gradient(135deg, #9333ea, #7e22ce);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
}

.btn-add-comment:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-add-comment svg {
  width: 1rem;
  height: 1rem;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #9CA3AF;
  gap: 1rem;
}

.loading-state p {
  margin: 0;
  font-size: 0.9rem;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #6B7280;
  gap: 0.75rem;
}

.empty-state svg {
  color: rgba(255, 255, 255, 0.1);
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
  color: #9CA3AF;
}

/* Comments container */
.comments-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Load more */
.load-more {
  padding: 1.5rem 0;
  text-align: center;
}

.loading-more {
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.loading-more svg {
  width: 1rem;
  height: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .add-comment-section {
    padding-bottom: 0.75rem;
  }

  .btn-add-comment {
    padding: 0.625rem 1rem;
    font-size: 0.85rem;
  }

  .loading-state,
  .empty-state {
    padding: 2rem 1rem;
  }
}
</style>
